import {
  isAgentItemUpdateEvent,
  type AgentPromptCommandRecord,
  type AgentRunStatus,
  type AgentTaskRecord,
  type AgentUpdateRecord,
  type AgentUpdateSummary,
} from "../../agent";

export const DEFAULT_AGENT_API_BASE_URL = "http://localhost:8000";
export const AGENT_UPDATE_MODE_STORAGE_KEY = "workanywhere.agentUpdateMode";
export const WORKSPACE_KEY_STORAGE_KEY = "workanywhere.workspaceKey";
const WORKSPACE_KEY_PATTERN = /^wk_[A-Za-z0-9_-]{43}$/;

export type AgentUpdateMode = "normal" | "developer";

export type AgentApiBaseUrlInput = {
  queryString: string;
  storedValue: string | null;
  envValue: string | undefined;
};

export type EmptyStateCopy = {
  title: string;
  description: string;
};

export type EventTone = "success" | "danger" | "info" | "neutral";
export type TaskDisplayStatus = AgentRunStatus | "no-runs";

export type AgentPromptFetcher = (input: string, init: RequestInit) => Promise<Response>;

export type PromptTimelineSource = "desktop" | "frontend" | "tui";

export type AgentTimelineEventEntry = {
  kind: "event";
  id: string;
  receivedAt: string;
  record: AgentUpdateRecord;
};

export type AgentTimelinePromptEntry = {
  kind: "prompt";
  id: string;
  receivedAt: string;
  taskId: string;
  runId: string | null;
  prompt: string;
  source: PromptTimelineSource;
  status?: AgentPromptCommandRecord["status"];
};

export type AgentTimelineEntry = AgentTimelineEventEntry | AgentTimelinePromptEntry;

const NORMAL_MODE_VISIBLE_ITEM_EVENTS: Record<string, Record<string, true>> = {
  "item.completed": {
    agent_message: true,
    command_execution: true,
  },
};

export type SubmitAgentPromptInput = {
  agentApiBaseUrl: string;
  workspaceKey: string;
  taskId: string;
  prompt: string;
  fetcher?: AgentPromptFetcher;
};

export type CanSubmitAgentPromptInput = {
  currentTaskId: string | null;
  prompt: string;
  isSubmitting: boolean;
};

export function getAgentApiBaseUrl({ queryString, storedValue, envValue }: AgentApiBaseUrlInput): string {
  const queryValue = new URLSearchParams(queryString).get("agentApiBaseUrl");

  return queryValue || storedValue || envValue || DEFAULT_AGENT_API_BASE_URL;
}

export async function submitAgentPrompt({
  agentApiBaseUrl,
  workspaceKey,
  taskId,
  prompt,
  fetcher = fetch,
}: SubmitAgentPromptInput): Promise<AgentPromptCommandRecord> {
  const response = await fetcher(`${agentApiBaseUrl}/agent/prompt`, {
    method: "POST",
    headers: { "content-type": "application/json", ...buildWorkspaceKeyHeaders(workspaceKey) },
    body: JSON.stringify({ task_id: taskId, prompt: prompt.trim() }),
  });

  if (!response.ok) {
    const details = await response.text();
    throw new Error(`Prompt request failed with ${response.status}${details ? `: ${details}` : ""}`);
  }

  const body = (await response.json()) as { command: AgentPromptCommandRecord };
  return body.command;
}

export function isValidWorkspaceKey(value: string): boolean {
  return WORKSPACE_KEY_PATTERN.test(value.trim());
}

export function buildWorkspaceKeyHeaders(workspaceKey: string): Record<string, string> {
  return { "x-workspace-key": workspaceKey };
}

export function clearStoredWorkspaceKey(storage: Pick<Storage, "removeItem"> = localStorage): void {
  storage.removeItem(WORKSPACE_KEY_STORAGE_KEY);
}

export function parseAgentUpdateSseRecords(input: string): AgentUpdateRecord[] {
  const records: AgentUpdateRecord[] = [];

  for (const record of input.split(/\n\n/)) {
    const lines = record.split(/\n/);
    const event = lines
      .find((line) => line.startsWith("event:"))
      ?.slice("event:".length)
      .trim();

    if (event !== "agent.update") {
      continue;
    }

    const data = lines
      .filter((line) => line.startsWith("data:"))
      .map((line) => line.slice("data:".length).trim())
      .join("\n");

    if (data) {
      records.push(JSON.parse(data) as AgentUpdateRecord);
    }
  }

  return records;
}

export function canSubmitAgentPrompt({
  currentTaskId,
  prompt,
  isSubmitting,
}: CanSubmitAgentPromptInput): boolean {
  return Boolean(currentTaskId) && prompt.trim().length > 0 && !isSubmitting;
}

export function getInitialAgentUpdateMode(storedValue: string | null): AgentUpdateMode {
  return storedValue === "developer" ? "developer" : "normal";
}

export function mergeAgentUpdateRecords(
  existingRecords: AgentUpdateRecord[],
  incomingRecords: AgentUpdateRecord[],
): AgentUpdateRecord[] {
  const recordsById = new Map<string, AgentUpdateRecord>();

  for (const record of existingRecords) {
    recordsById.set(record.id, record);
  }

  for (const record of incomingRecords) {
    recordsById.set(record.id, record);
  }

  return [...recordsById.values()].sort((a, b) => a.sequence - b.sequence);
}

export function getDefaultSelectedTaskId(tasks: AgentTaskRecord[], selectedTaskId: string | null): string | null {
  if (selectedTaskId && tasks.some((task) => task.id === selectedTaskId)) {
    return selectedTaskId;
  }

  return tasks[0]?.id ?? null;
}

export function getTaskDisplayStatus(task: AgentTaskRecord): TaskDisplayStatus {
  if (task.runs.length === 0) {
    return "no-runs";
  }

  if (task.runs.some((run) => run.status === "running")) {
    return "running";
  }

  const newestRun = [...task.runs].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];

  return newestRun?.status ?? "no-runs";
}

export function getTaskSourceLabel(task: AgentTaskRecord): string | null {
  return task.metadata.source === "tui" ? "TUI" : null;
}

export function filterAgentUpdateRecordsByTaskId(
  records: AgentUpdateRecord[],
  taskId: string | null,
): AgentUpdateRecord[] {
  return taskId ? records.filter((record) => record.event.task_id === taskId) : [];
}

export function shouldAppendLiveRecordToSelectedTask(record: AgentUpdateRecord, selectedTaskId: string | null): boolean {
  return record.event.task_id === selectedTaskId;
}

export function getTaskTimelineEmptyState(
  tasks: AgentTaskRecord[],
  selectedTask: AgentTaskRecord | null,
  records: readonly unknown[],
): EmptyStateCopy | null {
  if (tasks.length === 0) {
    return {
      title: "No tasks yet",
      description: "Tasks will appear here after Codex runs are created.",
    };
  }

  if (!selectedTask) {
    return {
      title: "Select a task",
      description: "Choose a task from the sidebar to inspect its timeline.",
    };
  }

  if (records.length === 0) {
    return {
      title: "No visible events",
      description: "This task has no events to show in the current view.",
    };
  }

  return null;
}

export function isAgentMessageRecord(record: AgentUpdateRecord): boolean {
  return isAgentItemUpdateEvent(record.event) && record.event.item.type === "agent_message";
}

export function isNormalModeVisibleAgentUpdateRecord(record: AgentUpdateRecord): boolean {
  if (!isAgentItemUpdateEvent(record.event)) {
    return false;
  }

  return NORMAL_MODE_VISIBLE_ITEM_EVENTS[record.event.type]?.[record.event.item.type] === true;
}

export function getVisibleAgentUpdateRecords(
  records: AgentUpdateRecord[],
  mode: AgentUpdateMode,
): AgentUpdateRecord[] {
  return mode === "normal" ? records.filter(isNormalModeVisibleAgentUpdateRecord) : records;
}

export function getVisibleAgentTimelineEntries({
  records,
  promptCommands,
  mode,
}: {
  records: AgentUpdateRecord[];
  promptCommands: AgentPromptCommandRecord[];
  mode: AgentUpdateMode;
}): AgentTimelineEntry[] {
  const entries: AgentTimelineEntry[] = promptCommands.map((command) => ({
    kind: "prompt",
    id: `prompt-command:${command.id}`,
    receivedAt: command.createdAt,
    taskId: command.taskId,
    runId: command.runId,
    prompt: command.prompt,
    source: "frontend",
    status: command.status,
  }));

  for (const record of records) {
    const startedPrompt = getStartedPrompt(record);

    if (startedPrompt && (mode !== "normal" || !hasMatchingPromptCommand(promptCommands, record, startedPrompt.prompt))) {
      entries.push({
        kind: "prompt",
        id: `${startedPrompt.source}-prompt:${record.id}`,
        receivedAt: record.receivedAt,
        taskId: record.event.task_id,
        runId: record.event.run_id,
        prompt: startedPrompt.prompt,
        source: startedPrompt.source,
      });
    }

    if (mode === "developer" || isNormalModeVisibleAgentUpdateRecord(record)) {
      entries.push({
        kind: "event",
        id: `event:${record.id}`,
        receivedAt: record.receivedAt,
        record,
      });
    }
  }

  return entries.sort((a, b) => new Date(a.receivedAt).getTime() - new Date(b.receivedAt).getTime());
}

function hasMatchingPromptCommand(
  promptCommands: AgentPromptCommandRecord[],
  record: AgentUpdateRecord,
  desktopPrompt: string,
): boolean {
  return promptCommands.some((command) => {
    if (command.runId && command.runId === record.event.run_id) {
      return true;
    }

    return (
      command.taskId === record.event.task_id &&
      normalizePrompt(command.prompt) === normalizePrompt(desktopPrompt) &&
      new Date(command.createdAt).getTime() <= new Date(record.receivedAt).getTime()
    );
  });
}

function normalizePrompt(value: string): string {
  return value.trim().replace(/\s+/g, " ");
}

function getStartedPrompt(record: AgentUpdateRecord): { prompt: string; source: "desktop" | "tui" } | null {
  if (record.event.type === "tui.started") {
    const prompt = record.event.prompt;
    return typeof prompt === "string" && prompt.trim() ? { prompt: prompt.trim(), source: "tui" } : null;
  }

  if (record.event.type !== "desktop.started") {
    return null;
  }

  const desktopEvent = record.event.desktop_event;
  if (!desktopEvent || typeof desktopEvent !== "object" || Array.isArray(desktopEvent)) {
    return null;
  }

  const prompt = (desktopEvent as Record<string, unknown>).prompt;
  return typeof prompt === "string" && prompt.trim() ? { prompt: prompt.trim(), source: "desktop" } : null;
}

export function isTurnInProgress(records: AgentUpdateRecord[]): boolean {
  let latestStartedSequence = -1;
  let latestFinishedSequence = -1;

  for (const record of records) {
    if (record.event.type === "turn.started") {
      latestStartedSequence = Math.max(latestStartedSequence, record.sequence);
    }

    if (record.event.type === "turn.completed" || record.event.type === "turn.failed") {
      latestFinishedSequence = Math.max(latestFinishedSequence, record.sequence);
    }
  }

  return latestStartedSequence > latestFinishedSequence;
}

export function getDefaultSelectedEventId(
  records: AgentUpdateRecord[],
  selectedEventId: string | null,
  mode: AgentUpdateMode,
): string | null {
  if (mode === "normal") {
    const visibleRecords = getVisibleAgentUpdateRecords(records, mode);

    return selectedEventId && visibleRecords.some((record) => record.id === selectedEventId) ? selectedEventId : null;
  }

  if (selectedEventId && records.some((record) => record.id === selectedEventId)) {
    return selectedEventId;
  }

  return records.at(-1)?.id ?? null;
}

export function formatEventTitle(record: AgentUpdateRecord): string {
  const { event } = record;

  if (event.type === "thread.started") {
    return "Thread started";
  }

  if (event.type === "turn.started") {
    return "Turn started";
  }

  if (event.type === "turn.completed") {
    return "Turn completed";
  }

  if (event.type === "turn.failed") {
    return "Turn failed";
  }

  if (event.type === "error") {
    return "Error";
  }

  if (isAgentItemUpdateEvent(event)) {
    return `Item ${event.type.replace("item.", "")}: ${String(event.item.type)}`;
  }

  return event.type;
}

export function summarizeEmptyState(records: AgentUpdateRecord[]): EmptyStateCopy | null {
  if (records.length > 0) {
    return null;
  }

  return {
    title: "Waiting for agent updates",
    description: "Accepted events from /agent/updates will appear here as they arrive.",
  };
}

export function summarizeRecords(records: AgentUpdateRecord[]): AgentUpdateSummary {
  return records.reduce<AgentUpdateSummary>(
    (summary, record) => {
      summary.total += 1;
      summary.counts[record.event.type] = (summary.counts[record.event.type] ?? 0) + 1;
      summary.currentTaskId = record.event.task_id;

      if (record.event.type === "thread.started") {
        summary.currentThreadId = record.event.thread_id;
      }

      if (record.event.type === "turn.completed") {
        summary.latestUsage = record.event.usage;
      }

      if (record.event.type === "error") {
        summary.latestError = record;
      }

      if (record.event.type === "turn.failed") {
        summary.latestFailure = record;
      }

      return summary;
    },
    {
      total: 0,
      counts: {},
      currentTaskId: null,
      currentThreadId: null,
      latestUsage: null,
      latestError: null,
      latestFailure: null,
    },
  );
}

export function getEventTone(record: AgentUpdateRecord): EventTone {
  if (record.event.type === "turn.failed" || record.event.type === "error") return "danger";
  if (record.event.type.endsWith(".completed") || record.event.type === "turn.completed") return "success";
  if (record.event.type.endsWith(".started") || record.event.type === "thread.started") return "info";
  return "neutral";
}

export function getEventPreview(record: AgentUpdateRecord): string {
  const { event } = record;

  if (event.type === "thread.started") return `Thread ${event.thread_id}`;
  if (event.type === "turn.completed") {
    const totalTokens = event.usage.input_tokens + event.usage.output_tokens;
    return `${totalTokens.toLocaleString()} total tokens`;
  }
  if (event.type === "turn.started") return "Agent turn is in progress";
  if (event.type === "turn.failed") return "Agent turn failed";
  if (event.type === "error") return "Agent reported an error";

  if (!isAgentItemUpdateEvent(event)) {
    return event.type;
  }

  if (event.item.type === "command_execution" && event.item.command) {
    return `Executed command: ${String(event.item.command)}`;
  }

  const text = event.item.text ?? event.item.command ?? event.item.status ?? event.item.id;

  return String(text);
}

export function getThreadId(record: AgentUpdateRecord): string | null {
  return record.event.type === "thread.started" ? record.event.thread_id : null;
}

export function getItemType(record: AgentUpdateRecord): string | null {
  return isAgentItemUpdateEvent(record.event) ? String(record.event.item.type) : null;
}

export function formatTime(value: string): string {
  return new Intl.DateTimeFormat(undefined, {
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
  }).format(new Date(value));
}

export function formatDateTime(value: string): string {
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "medium",
  }).format(new Date(value));
}
